import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Body, CodeBlock, MenuPath, Note, SectionDivider, Warning } from '../ui';

// ── Page ────────────────────────────────────────────────────────────────────

const ApiDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary">
      {/* -- Intro -- */}
      <h2
        id="python-api"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.api.title')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.api.intro1"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>{t('docs.api.intro2')}</Body>
      <ul className="text-[14px] leading-[1.75] text-content-secondary mb-[16px] pl-[20px]">
        <li>
          <Trans
            i18nKey="docs.api.moduleList1"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="docs.api.moduleList2"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="docs.api.moduleList3"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </li>
      </ul>

      <SectionDivider />

      {/* -- Export -- */}
      <h2
        id="export"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.api.exportTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.api.exportBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`import touchpose.touch_set_editor as tse

filepath = "/path/to/my_rig.touch"
warning = tse.export_sets(filepath)

if warning:
    print(warning)`}</CodeBlock>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.api.exportWhatItDoesTitle')}
      </h3>
      <ul className="text-[14px] leading-[1.75] text-content-secondary mb-[16px] pl-[20px]">
        <li>
          <Trans
            i18nKey="docs.api.exportItem1"
            components={{ strong: <strong /> }}
          />
        </li>
        <li>{t('docs.api.exportItem2')}</li>
        <li>
          <Trans
            i18nKey="docs.api.exportItem3"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </li>
        <li>{t('docs.api.exportItem4')}</li>
        <li>
          <Trans i18nKey="docs.api.exportItem5" components={{ em: <em /> }} />
        </li>
      </ul>

      <Note label={t('docs.api.noteLabel')}>
        <Trans
          i18nKey="docs.api.exportNote"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Note>

      <SectionDivider />

      {/* -- Import -- */}
      <h2
        id="import"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.api.importTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.api.importBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`import touchpose.touch_set_editor as tse

filepath = "/path/to/my_rig.touch"
warning = tse.import_sets(filepath)

if warning:
    print(warning)

# Rebuild render items so the imported sets become active
tse.build_touch_pose()`}</CodeBlock>

      <Warning label={t('docs.api.warningLabel')}>
        <Trans
          i18nKey="docs.api.importWarning"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Warning>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.api.importWhatItDoesTitle')}
      </h3>
      <ul className="text-[14px] leading-[1.75] text-content-secondary mb-[16px] pl-[20px]">
        <li>
          <Trans
            i18nKey="docs.api.importItem1"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="docs.api.importItem2"
            components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
          />
        </li>
        <li>{t('docs.api.importItem3')}</li>
        <li>{t('docs.api.importItem4')}</li>
      </ul>

      <h3 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.api.buildTouchPoseTitle')}
      </h3>
      <Body>{t('docs.api.buildTouchPoseBody')}</Body>

      <CodeBlock>{`import touchpose.touch_set_editor as tse

# Rebuild from whatever sets are currently in the scene
tse.build_touch_pose()`}</CodeBlock>

      <Note label={t('docs.api.noteLabel')}>
        <Trans
          i18nKey="docs.api.buildTouchPoseNote"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Note>

      <SectionDivider />

      {/* -- Touch Sets -- */}
      <h2
        id="touch-sets"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.api.touchSetsTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.api.touchSetsBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 className="font-display text-[17px] font-bold text-content-primary mt-[24px] mb-[8px]">
        {t('docs.api.createSetTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.api.createSetBody1"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.api.createSetBody2"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`import touchpose.touch_set_editor as tse

# Create touch sets for a list of controls
controls = ["L_hand", "R_hand", "L_foot", "R_foot"]
new_sets = tse.create_set(controls)
print(new_sets)
# ['L_hand_touch', 'R_hand_touch', 'L_foot_touch', 'R_foot_touch']

# Rebuild so the new sets become active
tse.build_touch_pose()`}</CodeBlock>

      <Note label={t('docs.api.noteLabel')}>
        <Trans
          i18nKey="docs.api.createSetNote"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Note>

      <h3 className="font-display text-[17px] font-bold text-content-primary mt-[32px] mb-[8px]">
        {t('docs.api.deleteSetTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.api.deleteSetBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`import touchpose.touch_set_editor as tse

tse.delete_set("L_hand_touch")

# Rebuild to reflect the removal
tse.build_touch_pose()`}</CodeBlock>

      <Warning label={t('docs.api.warningLabel')}>
        <Trans
          i18nKey="docs.api.deleteSetWarning"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Warning>

      <SectionDivider />

      {/* -- Add / Remove Faces -- */}
      <h2
        id="add-remove-faces"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.api.addRemoveFacesTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.api.addRemoveFacesBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <h3 className="font-display text-[17px] font-bold text-content-primary mt-[24px] mb-[8px]">
        {t('docs.api.applyEditTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.api.applyEditBody1"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.api.applyEditBody2"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`import touchpose.touch_set_editor as tse

# Add selected faces to a touch set
import maya.cmds as mc
selected_faces = mc.filterExpand(mc.ls(sl=True), sm=34) or []

tse.apply_edit(
    face_ids=selected_faces,
    touch_set="L_hand_touch",
)`}</CodeBlock>

      <Note label={t('docs.api.noteLabel')}>
        <Trans
          i18nKey="docs.api.applyEditNote"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Note>

      <h3 className="font-display text-[17px] font-bold text-content-primary mt-[32px] mb-[8px]">
        {t('docs.api.removeMembersTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.api.removeMembersBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`import touchpose.touch_set_editor as tse
import maya.cmds as mc

# Remove selected faces from all sets
selected_faces = mc.filterExpand(mc.ls(sl=True), sm=34) or []

tse.remove_members_from_all(selected_faces)

# Rebuild to reflect the removal
tse.build_touch_pose()`}</CodeBlock>

      <Warning label={t('docs.api.warningLabel')}>
        <Trans
          i18nKey="docs.api.removeMembersWarning"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Warning>

      <SectionDivider />

      {/* -- Actions -- */}
      <h2
        id="actions"
        className="font-display text-[24px] font-extrabold text-content-primary  mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.api.actionsTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.api.actionsBody1"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>{t('docs.api.actionsBody2')}</Body>
      <ul className="text-[14px] leading-[1.75] text-content-secondary mb-[16px] pl-[20px]">
        <li>
          <Trans
            i18nKey="docs.api.actionsLevel1"
            components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="docs.api.actionsLevel2"
            components={{ strong: <strong /> }}
          />
        </li>
      </ul>

      <h3 className="font-display text-[17px] font-bold text-content-primary mt-[24px] mb-[8px]">
        {t('docs.api.getCollectionTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.api.getCollectionBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`import touchpose.touch_commands as tc

commands = tc.get_collection_commands()
for key, cmd in commands.items():
    print(f"[{cmd['index']}] {cmd['name']}")`}</CodeBlock>

      <h3 className="font-display text-[17px] font-bold text-content-primary mt-[32px] mb-[8px]">
        {t('docs.api.setCollectionTitle')}
      </h3>
      <Body>{t('docs.api.setCollectionBody')}</Body>

      <CodeBlock>{`import touchpose.touch_commands as tc

script = """
import maya.cmds as mc
mc.select(touch_control)
"""

index = tc.set_collection_command(name="Select Control", script=script)
print(f"Created action at index {index}")`}</CodeBlock>

      <h3 className="font-display text-[17px] font-bold text-content-primary mt-[32px] mb-[8px]">
        {t('docs.api.remCollectionTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.api.remCollectionBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`import touchpose.touch_commands as tc

commands = tc.get_collection_commands()
for cmd in commands.values():
    if cmd["name"] == "Select Control":
        tc.rem_collection_command(cmd["attr"])
        break`}</CodeBlock>

      <h3 className="font-display text-[17px] font-bold text-content-primary mt-[32px] mb-[8px]">
        {t('docs.api.setItemTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.api.setItemBody1"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.api.setItemBody2"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`import touchpose.touch_commands as tc
import touchpose.constants as constants

# Find the action we want to assign
commands = tc.get_collection_commands()
select_cmd = next(c for c in commands.values() if c["name"] == "Select Control")

# Assign it to L_hand on right-click
tc.set_item_command(
    touch_obj="L_hand",
    collection_attr=select_cmd["attr"],
    event=constants.TOUCH_EVENT["right_click"],  # 0
)

# Clear the right-click assignment on R_hand
tc.set_item_command(
    touch_obj="R_hand",
    collection_attr=select_cmd["attr"],
    event=constants.TOUCH_EVENT["right_click"],
    set_to_none=True,
)`}</CodeBlock>

      <h3 className="font-display text-[17px] font-bold text-content-primary mt-[32px] mb-[8px]">
        {t('docs.api.getItemTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.api.getItemBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`import touchpose.touch_commands as tc

item_commands = tc.get_item_commands("L_hand")
for cmd in item_commands.values():
    print(f"Event {cmd['event']}: {cmd['name']} (enabled={cmd['enabled']})")`}</CodeBlock>

      <SectionDivider />

      {/* -- Draw Pose from an action -- */}
      <h2
        id="draw-pose-entry"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.api.drawPoseEntryTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.api.drawPoseEntryBody1"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`from touchpose import draw_pose

# 'touch_control' is the variable an action snippet sees — the short
# name of the control whose touchSet was clicked.
draw_pose.enter_with_control(touch_control)`}</CodeBlock>

      <Body>
        <Trans
          i18nKey="docs.api.drawPoseEntryBody2"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <SectionDivider />

      {/* -- Rename Control -- */}
      <h2
        id="rename-control"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.api.renameControlTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.api.renameControlBody1"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.api.renameControlBody2"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`import touchpose.touch_set_editor as tse
import maya.cmds as mc

# Rename the Maya control first (or earlier in your rig-build step).
mc.rename("brow_bend_1_r", "brow_bend_1_r_renamed")

# Sync every layer's touch set, touch data node, and org-group /
# draw-group member reference to the new name.
renamed = tse.rename_controls_touch_data(
    "brow_bend_1_r",
    "brow_bend_1_r_renamed",
)
print(renamed)  # ['brow_bend_1_r_renamed_touch', 'L1__brow_bend_1_r_renamed_touch', ...]`}</CodeBlock>

      <Note label={t('docs.api.noteLabel')}>
        <Trans
          i18nKey="docs.api.renameControlNote"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Note>

      <SectionDivider />

      {/* -- Set Active Layer -- */}
      <h2
        id="set-active-layer"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.api.setActiveLayerTitle')}
      </h2>
      <Body>
        <Trans
          i18nKey="docs.api.setActiveLayerBody1"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.api.setActiveLayerBody2"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>

      <CodeBlock>{`import touchpose.touch_layers as tl

# Activate by name on the user's currently-selected Touch Node
tl.set_active_layer(layer="Face")

# Activate by index on a specific touchCollection
tl.set_active_layer("char01:touchpose_grp", 0)

# Returns the resolved integer layer index, or -1 if it couldn't
# resolve the collection or layer name.
idx = tl.set_active_layer(layer="Layer 1")
print(idx)`}</CodeBlock>

      <Note label={t('docs.api.noteLabel')}>
        <Trans
          i18nKey="docs.api.setActiveLayerNote"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Note>
    </article>
  );
};

export default ApiDoc;
